/**
 * Mega Energy and the rest period as pictures (2026-09-03 owner ask: a
 * number gets a picture before it gets a sentence).
 *
 *   MegaEnergyRing   the form's energy as a ring filling toward its next
 *                    evolve cost, GO's energy gem in the centre. A full ring
 *                    means evolve now; a short one carries the gap as the
 *                    number on the ring, not a sentence.
 *   MegaRestRing     the rest period emptying from the last Mega Evolution;
 *                    "free now" when it is done.
 *   MegaProgressBar  evolutions toward the next level ("3 / 7 to High"),
 *                    from an exact count when a page said so, else the
 *                    fraction the level circles showed.
 *
 * One token colour each (--mega-energy for energy, --mega for the level)
 * so both themes read them the way the game does.
 */
import { MegaEnergyIcon } from "@/components/ResourceCost";
import { MegaIcon } from "@/components/MegaIcon";
import { megaLevelProgress, type MegaFormRef, type MegaLevel } from "@/lib/megaLevels";
import { megaRestRemainingMs, type MegaLevelEntry } from "@/lib/megaLevelStore";

function arc(fraction: number, r: number): { dasharray: string } {
  const c = 2 * Math.PI * r;
  const f = Math.max(0, Math.min(1, fraction));
  return { dasharray: `${f * c} ${c}` };
}

/** "2d 4h" / "5h 12m" / "40m". */
export function shortDuration(ms: number): string {
  const m = Math.max(0, Math.floor(ms / 60_000));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${mm}m`;
  return `${mm}m`;
}

/** What the next evolution costs: first-time until the form has been evolved, then the repeat cost. */
export function megaNextCost(form: MegaFormRef, evolvedBefore: boolean): number | null {
  if (!form.stat) return null;
  return evolvedBefore ? form.stat.energy : form.stat.firstEnergy;
}

export function MegaEnergyRing({
  form,
  energy,
  evolvedBefore,
  size = 64,
  testId,
}: {
  form: MegaFormRef;
  energy: number | null;
  evolvedBefore: boolean;
  size?: number;
  testId?: string;
}) {
  const cost = megaNextCost(form, evolvedBefore);
  const fraction = energy != null && cost != null && cost > 0 ? Math.min(1, energy / cost) : energy != null ? 1 : 0;
  const full = energy != null && cost != null && energy >= cost;
  const short = energy != null && cost != null && !full ? cost - energy : null;
  const r = 44;
  const { dasharray } = arc(fraction, r);
  const ringColor = full ? "hsl(var(--mega))" : "hsl(var(--mega-energy))";
  return (
    <div
      className="flex flex-col items-center gap-1"
      data-testid={testId ?? `mega-energy-ring-${form.key}`}
      data-fraction={Math.round(fraction * 100)}
      data-full={full ? "true" : undefined}
      title={
        energy == null
          ? `${form.name}: energy not read yet`
          : cost == null
            ? `${form.name}: ${energy.toLocaleString()} energy`
            : full
              ? `${form.name}: ${energy.toLocaleString()} energy, enough to Mega Evolve (${cost.toLocaleString()})`
              : `${form.name}: ${energy.toLocaleString()} of ${cost.toLocaleString()} energy`
      }
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" width={size} height={size} className="absolute inset-0">
          <circle cx="50" cy="50" r={r} fill="none" stroke="var(--surface-sunken)" strokeWidth="9" />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={ringColor}
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={dasharray}
            transform="rotate(-90 50 50)"
            style={{ transition: "stroke-dasharray 400ms ease", filter: full ? "drop-shadow(0 0 4px hsl(var(--mega) / 0.6))" : undefined }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <MegaEnergyIcon size={Math.round(size * 0.3)} />
          {short != null && (
            <span
              className="text-[9px] font-black tabular-nums leading-none mt-0.5"
              style={{ color: "var(--text-tertiary)" }}
              data-testid={`${testId ?? `mega-energy-ring-${form.key}`}-short`}
            >
              -{short.toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <span
        className="text-[12px] font-black tabular-nums leading-none"
        style={{ color: energy == null ? "var(--text-tertiary)" : full ? "hsl(var(--mega))" : "var(--text-primary)" }}
      >
        {energy == null ? "?" : energy.toLocaleString()}
      </span>
      <span className="text-[8px] font-bold uppercase tracking-wider leading-none" style={{ color: "var(--text-tertiary)" }}>
        {full ? "Evolve now" : cost != null ? `of ${cost.toLocaleString()}` : form.formLabel ? `Energy ${form.formLabel}` : "Energy"}
      </span>
    </div>
  );
}

export function MegaRestRing({
  entry,
  now,
  size = 64,
  testId,
}: {
  entry: MegaLevelEntry | undefined;
  now: number;
  size?: number;
  testId?: string;
}) {
  const remaining = megaRestRemainingMs(entry, now);
  const total =
    entry?.restEndsAt && entry.lastActivatedAt ? entry.restEndsAt - entry.lastActivatedAt : null;
  // Without a start (a rest read off a page), the ring shows the known
  // remainder against the level's full rest.
  const fraction = remaining === 0 ? 0 : total && total > 0 ? Math.min(1, remaining / total) : 1;
  const free = remaining === 0;
  const r = 44;
  const { dasharray } = arc(fraction, r);
  const known = !!entry?.restEndsAt;
  return (
    <div
      className="flex flex-col items-center gap-1"
      data-testid={testId ?? "mega-rest-ring"}
      data-free={free ? "true" : undefined}
      data-remaining={remaining}
      data-known={known ? "true" : "false"}
      title={
        free
          ? known
            ? "No rest running: the next Mega Evolution is free of energy"
            : "No rest read yet: scan one of yours to learn when it is free"
          : `Rest period: ${shortDuration(remaining)} left`
      }
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" width={size} height={size} className="absolute inset-0">
          <circle cx="50" cy="50" r={r} fill="none" stroke="var(--surface-sunken)" strokeWidth="9" />
          {!free && (
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="var(--text-tertiary)"
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={dasharray}
              transform="rotate(-90 50 50)"
            />
          )}
          {free && known && (
            <circle cx="50" cy="50" r={r} fill="none" stroke="var(--accent-fill)" strokeWidth="9" opacity="0.7" />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <MegaIcon size={Math.round(size * 0.34)} style={{ color: free && known ? "var(--accent-fill)" : "var(--text-tertiary)" }} />
        </div>
      </div>
      <span
        className="text-[12px] font-black tabular-nums leading-none"
        style={{ color: free ? (known ? "var(--accent-fill)" : "var(--text-tertiary)") : "var(--text-primary)" }}
      >
        {free ? (known ? "Free" : "Free?") : shortDuration(remaining)}
      </span>
      <span className="text-[8px] font-bold uppercase tracking-wider leading-none" style={{ color: "var(--text-tertiary)" }}>
        {free ? (known ? "to evolve" : "scan to read") : "rest left"}
      </span>
    </div>
  );
}

export function MegaProgressBar({
  level,
  entry,
  testId,
}: {
  level: MegaLevel | null;
  entry: MegaLevelEntry | undefined;
  testId?: string;
}) {
  const prog = megaLevelProgress({ level, evolutions: entry?.evolutions, progressToNext: entry?.progressToNext });
  if (level == null) return null;
  if (!prog.next) {
    return (
      <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "hsl(var(--mega))" }} data-testid={testId}>
        {level === 4 ? "Super Max reached" : "Max reached. Super Max is for designated Pokémon."}
      </p>
    );
  }
  const pct = prog.fraction != null ? Math.round(prog.fraction * 100) : null;
  return (
    <div data-testid={testId} data-done={prog.done ?? undefined} data-needed={prog.needed ?? undefined}>
      <div className="flex items-baseline justify-between">
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
          To {prog.next.label}
        </span>
        <span className="text-[10px] font-black tabular-nums" style={{ color: "hsl(var(--mega))" }}>
          {prog.done != null ? `${prog.done} / ${prog.needed}` : pct != null ? `${pct}%` : `${prog.needed} evolutions`}
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-sunken)" }}>
        <div
          className="h-full rounded-full"
          style={{ width: `${pct ?? 0}%`, background: "hsl(var(--mega))", transition: "width 400ms ease" }}
        />
      </div>
    </div>
  );
}
